import { useEffect, useState } from 'react';
import { Badge, Form, ListGroup, Modal, Placeholder } from 'react-bootstrap';
import { getLocalDateTimeStr, getRelativeDate, getRelativeDateWithTimeAfterHour } from '../../../../utils/time';
import { PatientVisit } from '../../../../amplify/api-gateway/search-patients';
import { callQuery, GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE, GET_FILE_AT_VERSION, PatientFileVersion } from '../../../../amplify/api-gateway/query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface VersionSelectModalProps {
    patient: PatientVisit;
    fileType: string;
    defaultPatientFileVersion: PatientFileVersion;
    handleFileVersionChange: (fileType: string, fv: PatientFileVersion, prevFv: PatientFileVersion | null) => void;
    sectionToSetter: any;
}

const VersionSelectModal = ({ patient, fileType, defaultPatientFileVersion, handleFileVersionChange, sectionToSetter }: VersionSelectModalProps) => {
    const [show, setShow] = useState<boolean>(false);
    const [isLoadingVersions, setIsLoadingVersions] = useState<boolean>(false);
    const [isLoadingData, setIsLoadingData] = useState<any>();
    const [versions, setVersions] = useState<PatientFileVersion[]>([]);
    const [activePatientFileVersion, setActivePatientFileVersion] = useState<PatientFileVersion>(defaultPatientFileVersion);

    useEffect(() => {
        setActivePatientFileVersion(defaultPatientFileVersion);
    }, [defaultPatientFileVersion]);

    useEffect(() => {
        if (show) {
            setIsLoadingVersions(true);
            callQuery({
                operation: GET_ALL_VERSIONS_FOR_CSN_AND_FILE_TYPE,
                csn: patient.csn,
                file_type: fileType,
            })
            .then((fetched: PatientFileVersion[] | any) => {
                const safeVersions = Array.isArray(fetched) ? fetched : [];
                setVersions(safeVersions);
            })
            .finally(() => {
                setIsLoadingVersions(false);
            });
        }
    }, [show]);

    const handleVersionClick = (version: PatientFileVersion) => {
        setActivePatientFileVersion(version);
        const selectorKey = fileType.replace("explain_", "");
        if (sectionToSetter) {
            setIsLoadingData(version.version_id);

            // Find previous version
            const sortedVersions = [...versions].sort((a, b) => new Date(a.version_date).getTime() - new Date(b.version_date).getTime());
            const selectedIndex = sortedVersions.findIndex(v => v.version_id === version.version_id);
            const previousVersion = selectedIndex > 0 ? sortedVersions[selectedIndex - 1] : null;

            callQuery({
                operation: GET_FILE_AT_VERSION,
                mrn: patient.mrn,
                csn: patient.csn,
                file_type: fileType,
                version_id: version.version_id,
            })
            .then((data: any) => {
                sectionToSetter[selectorKey](data);
                handleFileVersionChange(fileType, version, previousVersion);
            })
            .finally(() => {
                setIsLoadingData(undefined);
            });
        }
        setShow(false);
    };

    return (
        <>
            <Modal show={show} onHide={() => {setShow(false)}} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select a File Version</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoadingVersions ? (
                        <ListGroup>
                            {
                                [1, 2, 3].map((v) => (
                                    <ListGroup.Item key={v}>
                                        <Placeholder animation="glow">
                                            <Placeholder xs={6} size="xs" />
                                        </Placeholder>
                                    </ListGroup.Item>
                                ))
                            }
                        </ListGroup>
                    ) : versions.length === 0 ? (
                        <p>No file versions available.</p>
                    ) : (
                        <ListGroup>
                            {versions.map((version: any, idx: number) => {
                                let isAfterDeparture = "";
                                if (patient.departure_time && (new Date(version.version_date) > new Date(patient.departure_time))) {
                                    isAfterDeparture = "(after departure)";
                                }
                                return (
                                    <ListGroup.Item
                                        key={version.version_id}
                                        action
                                        onClick={() => handleVersionClick(version)}
                                    >
                                        {
                                            isLoadingData === version.version_id ? (
                                                <><FontAwesomeIcon icon={faSpinner} spin /> Loading...</>
                                            ) : (
                                                <>Version {versions.length - idx} &bull; {getLocalDateTimeStr(version.version_date)} {isAfterDeparture} {version.version_id === defaultPatientFileVersion.version_id && (<>&bull; <span className="notes-highlight notes-highlight-green">Active</span></>)}</>
                                            )
                                        }
                                    </ListGroup.Item>
                                )
                            })}
                        </ListGroup>
                    )}
                </Modal.Body>
            </Modal>
            <span className="notes-highlight notes-highlight-orange" onClick={() => {setShow(true)}}>{getRelativeDateWithTimeAfterHour(new Date(activePatientFileVersion.version_date))}</span>
        </>
    );
};

export default VersionSelectModal;
